/**
 * Cache updaters for a table's fields.
 *
 * `qk.fields(tableId)` holds the whole list, and the four field events are each
 * a change to one entry in it -- plus `related_fields`, which is the part worth
 * naming: a link row field created in one table creates its counterpart in
 * another, so one event can change the schema of two tables at once, and each
 * carries the table it belongs to.
 *
 * Ported from `web-frontend/modules/database/store/field.js`.
 */
import type { Field } from '@/api/types'

/**
 * Adds a field, or replaces the one already held with that id.
 *
 * Upsert rather than insert, for the reason the tables' and views' are: a field
 * restored from the trash arrives as a creation, and this tab may still be
 * holding the copy it had before the field was deleted.
 */
export function upsertField(fields: readonly Field[], field: Field): Field[] {
  const index = fields.findIndex((held) => held.id === field.id)
  if (index === -1) return [...fields, field]

  const next = [...fields]
  next[index] = field
  return next
}

export function removeField(fields: readonly Field[], fieldId: number): Field[] {
  const next = fields.filter((field) => field.id !== fieldId)
  return next.length === fields.length ? (fields as Field[]) : next
}

/**
 * Groups fields by the table they belong to.
 *
 * `related_fields` is a flat list that can span tables, and each cached list is
 * keyed by one table -- so an event that touches two tables is two writes, and
 * this is what decides which field goes into which.
 */
export function byTable(fields: readonly Field[]): Map<number, Field[]> {
  const grouped = new Map<number, Field[]>()
  for (const field of fields) {
    const held = grouped.get(field.table_id)
    if (held === undefined) grouped.set(field.table_id, [field])
    else held.push(field)
  }
  return grouped
}
